import React, { ReactNode, useState } from "react";

import classNames from "classnames";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Img } from "src/app/components/Img/Img";
import Tabs from "src/app/components/Tabs/Tabs";
import Tab from "src/app/components/Tabs/Tab";
import { getAOChartData } from "src/app/project-details/tabs/AudienceOverlapTab/utils";
import { OverlapDataChart } from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/AudienceOverlapPane/OverlapDataChart/OverlapDataChart";
import {
  CDTable,
  CDTableColumn,
  CDTableRow,
} from "src/app/pages/CreatorDiscoveryTool/legacy/app/components/CDTable/CDTable";
import WarningInfo from "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/AudiencesResults/AudienceResultLoading/WarningInfo";
import METHODS from "src/app/pages/CreatorDiscoveryTool/legacy/app/helpers/methods";
import creatorPlaceholder from "src/images/creator-placeholder.svg";
import { SOCIAL_ICONS, SOCIAL_LABELS } from "src/types";

import AudienceOverlapEmptyState from "./AudienceOverlapEmptyState";
import "./AudienceOverlapResult.scss";
import { AudienceOverlapProviderResult, AudienceOverlapRow } from "./types";

import "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/AudienceOverlapPane/AudienceOverlapPane.scss";

const COLUMNS = [
  { id: "front_username", defaultMessage: "Username", toLeft: true },
  { id: "front_followers", defaultMessage: "Followers" },
  {
    id: "front_overlapping",
    defaultMessage: "Overlapping",
    tooltip: {
      id: "front_overlapping_followers_tooltip",
      defaultMessage:
        "Percentage of followers by this Creator that overlap with one or more ofthe Creator on the list.",
    },
  },
  {
    id: "front_unique",
    defaultMessage: "Unique",
    tooltip: {
      alignRight: true,
      id: "front_unique_followers_tooltip",
      defaultMessage:
        "Percentage of followers by this Creator that are not featured by any other Creator on this list.",
    },
  },
];

function Creator({ row }: { row: AudienceOverlapRow }) {
  return (
    <CDTableColumn className="audience-overlap-table__username">
      <div className="influencer-photo">
        <Img
          className={classNames(
            "influencer-photo__img",
            "cdt-table__image-content",
            "cdt-table__image",
          )}
          src={row.avatarUrl ?? undefined}
          fallbackImageSrc={creatorPlaceholder}
        />
      </div>
      <div className="influencer-info">
        <div className="influencer-info__username">
          <span>{row.name}</span>
        </div>
      </div>
    </CDTableColumn>
  );
}

function Amount({
  type,
  value,
}: {
  type: "followers" | "overlapping" | "unique";
  value: string;
}) {
  return (
    <CDTableColumn className="">
      <span
        className={`audience-overlap-table__amount audience-overlap-table__amount--${type}`}
      >
        {value}
      </span>
    </CDTableColumn>
  );
}

function ProviderResult({ result }: { result: AudienceOverlapProviderResult }) {
  if (result.message) {
    return <AudienceOverlapEmptyState text={result.message} />;
  }

  return (
    <>
      {result.reportInfo && (
        <OverlapDataChart
          chartData={getAOChartData(result.reportInfo)}
          overlapData={{ reportInfo: result.reportInfo }}
        />
      )}
      <div className="audience-overlap-results">
        <CDTable columns={COLUMNS} autoLayout showNoResults={false}>
          {result.rows.map((row) => (
            <CDTableRow
              key={row.key}
              data-qa={row.name}
              className=""
              highlighted={false}
            >
              <Creator row={row} />
              {row.errorMessage ? (
                <CDTableColumn className="" colSpan="3">
                  <WarningInfo
                    errorMessage={undefined}
                    error={
                      <IDHFormattedMessage
                        id={`front_missing_audience_data_${row.errorMessage}`}
                        defaultMessage={row.errorMessage}
                      />
                    }
                  />
                </CDTableColumn>
              ) : (
                <>
                  <Amount
                    type="followers"
                    value={METHODS.splitNumbers(row.followers)}
                  />
                  <Amount
                    type="overlapping"
                    value={METHODS.splitPercents(
                      1 - (row.uniquePercentage ?? 0),
                      " ",
                      1,
                    )}
                  />
                  <Amount
                    type="unique"
                    value={METHODS.splitPercents(
                      row.uniquePercentage ?? 0,
                      " ",
                      1,
                    )}
                  />
                </>
              )}
            </CDTableRow>
          ))}
        </CDTable>
      </div>
    </>
  );
}

interface Props {
  results: AudienceOverlapProviderResult[];
  // shown on the right of the tabs, e.g. the download button
  actions?: ReactNode;
}

/**
 * Result of audience overlap: a tab per social provider with the summary, the chart and the creators.
 * Shared by the campaign and by the Creator Search.
 */
export default function AudienceOverlapResult({ results, actions }: Props) {
  const [selectedProvider, setSelectedProvider] = useState(
    results[0]?.provider,
  );
  const selectedResult =
    results.find((result) => result.provider === selectedProvider) ??
    results[0];

  if (!selectedResult) return null;

  return (
    <>
      <div className="audience-overlap-result__header">
        <Tabs>
          {results.map(({ provider }) => (
            <Tab
              key={provider}
              tabText={
                <span className="social-data__tab">
                  <img src={SOCIAL_ICONS[provider]} alt={provider} />
                  {SOCIAL_LABELS[provider]}
                </span>
              }
              active={selectedResult.provider === provider}
              onClick={() => setSelectedProvider(provider)}
            />
          ))}
        </Tabs>
        {actions}
      </div>
      <ProviderResult result={selectedResult} />
    </>
  );
}
